import { Dialog, makeStyles, Theme, Typography } from '@material-ui/core';
import CustomButton from './CustomButton';
import React from 'react';
import PropTypes from 'prop-types';
import useGetBrowserHeight from '../hooks/useGetBrowserHeight';
import { ReactNode } from 'react';

type ModalProps = PropTypes.InferProps<typeof propTypes> & typeof defaultProps;

const useDialogStyles = makeStyles<Theme, StylesProps>(theme => ({
  paper: {
    border: '3px solid rgba(0, 0, 0, 0.0)',
    width: ({ isFireFoxBrowser }) =>
      isFireFoxBrowser ? '-moz-available' : 'fill-available',
    maxWidth: ({ maxWidth }) => `${maxWidth + 40}px`,
    borderRadius: '20px 20px 0 0',
    minHeight: '20px',
    boxShadow: '0 4px 12px 0 rgba(0, 0, 0, 0.3)',
    margin: '20px 0 0 0',
    textAlign: 'center',
    maxHeight: '90%',
    height: '90%',
    padding: '24px',
    position: 'fixed',
    bottom: '0',
    [theme.breakpoints.up('sm')]: {
      padding: '40px',
      height: 'auto',
      maxHeight: '760px',
      margin: '20px',
      borderRadius: '20px',
      bottom: 'initial',
      position: 'relative',
    },
  },
  paperScrollPaper: {
    display: 'flex',
    flexDirection: 'column',
    maxHeight: '90%',
    [theme.breakpoints.up('sm')]: {
      maxHeight: ({ browserHeight }) =>
        browserHeight > 760 + modalDefaultHeightMargin
          ? '760px'
          : `calc(100% - ${modalDefaultHeightMargin}px)`,
    },
  },
}));

const useStyle = makeStyles(theme => ({
  editButton: {
    borderRadius: '20px',
    left: '50%',
    bottom: '0',
    position: 'absolute',
    width: '90%',
    transform: `translate(-50%, -50%)`,
    [theme.breakpoints.up('sm')]: {
      left: '0',
      bottom: 'inherit',
      position: 'relative',
      transform: 'unset',
      fontWeight: 'normal',
      borderRadius: '20px',
      width: '100%',
    },
    '& p': {
      height: '20px',
    },
  },
  editLabelButton: {
    fontWeight: 'normal',
    color: '#000000',
  },
  editTitleLabel: {
    padding: '0',
    marginBottom: '16px',
    [theme.breakpoints.up('sm')]: {
      padding: '0',
      marginBottom: '16px',
    },
  },
  editBodyInput: {
    padding: '0',
    marginBottom: '12px',
    [theme.breakpoints.up('sm')]: {
      padding: '0',
      marginBottom: '12px',
    },
  },
}));

export const PartialModalOverlayTitle = ({
  children,
}: {
  children: ReactNode;
}) => (
  <Typography variant="subtitle1" className={useStyle().editTitleLabel}>
    {children}
  </Typography>
);
export const PartialModalOverlayButton = ({
  children,
}: {
  children: ReactNode;
}) => (
  <CustomButton size="medium" className={useStyle().editButton}>
    <span className={useStyle().editLabelButton}>{children}</span>
  </CustomButton>
);

export const PartialModalOverlayBody = ({
  children,
}: {
  children: ReactNode;
}) => (
  <Typography variant="subtitle2" className={useStyle().editBodyInput}>
    {children}
  </Typography>
);
const modalDefaultHeightMargin = 64;

interface StylesProps {
  maxWidth: number;
  browserHeight: number;
  isFireFoxBrowser: boolean;
}

function PartialModalOverlay({
  children,
  onClose,
  maxWidth,
  open,
  className,
  ...other
}: ModalProps) {
  const browserHeight = useGetBrowserHeight();
  const isFireFoxBrowser = /Firefox/.test(navigator.userAgent);
  const { paper, paperScrollPaper } = useDialogStyles({
    maxWidth,
    browserHeight,
    isFireFoxBrowser,
  });

  return (
    <Dialog
      onClose={onClose}
      open={open}
      {...other}
      classes={{ paper: `${paper} ${className || ''}`, paperScrollPaper }}
    >
      {children}
    </Dialog>
  );
}

const defaultProps = {
  maxWidth: 680,
};

const propTypes = {
  /** Custom content */
  children: PropTypes.node.isRequired,
  onClose: PropTypes.func.isRequired,
  maxWidth: PropTypes.number,
  open: PropTypes.bool.isRequired,
  className: PropTypes.string,
};

PartialModalOverlay.propTypes = propTypes;
PartialModalOverlay.defaultProps = defaultProps;

export default PartialModalOverlay;
